import type { ImageSearchHit } from '../lib/types'
import { DialogDescription, DialogHeader, DialogTitle } from '@ttab/elephant-ui'
import { ExternalLinkIcon } from '@ttab/elephant-ui/icons'
import type { JSX, ReactNode } from 'react'

// A smaller share of narrow (zoomed in) windows than of wide ones, kept within the free space
// beside the view (`--preview-space`) and short enough to leave room for the caption
const WIDTH = 'w-[min(55vw_-_16rem,var(--preview-space,100vw)_-_4rem,(90dvh_-_12rem)_*_16/9)]'

export const Preview = ({ hit, overlay }: {
  hit: ImageSearchHit
  overlay?: ReactNode
}): JSX.Element => {
  // A 16:9 image and a fixed caption height keep the centred dialog, and its nav buttons, in place
  return (
    <div className={`flex flex-col min-w-80 ${WIDTH}`}>
      <div className='relative bg-muted aspect-video'>
        {/* Keyed so the previous image isn't shown while the next one loads */}
        <img
          key={hit.id}
          src={hit.previewUrl}
          className='absolute inset-0 size-full object-contain'
        />
        <a
          href={hit.previewUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='absolute right-2 bottom-2 rounded-full bg-background/80 backdrop-blur-sm p-1.5 shadow-sm hover:bg-background transition-colors'
        >
          <ExternalLinkIcon size={16} strokeWidth={1.75} />
        </a>
        {overlay}
      </div>
      <div className='p-4 flex flex-col gap-2 h-48'>
        <DialogHeader className='shrink-0'>
          <DialogTitle className='text-base leading-snug line-clamp-2' title={hit.headline}>
            {hit.headline}
          </DialogTitle>
        </DialogHeader>
        {hit.description && (
          <DialogDescription className='min-h-0 flex-1 overflow-auto text-sm text-muted-foreground'>
            {hit.description}
          </DialogDescription>
        )}
        {hit.byline && (
          <p className='shrink-0 mt-auto text-xs text-muted-foreground'>
            {hit.byline}
          </p>
        )}
      </div>
    </div>
  )
}
